import React from 'react';
import { CustomizedTypography } from '@components/Customized';
import { colorSet } from '@components/Provider';
import {
  Radio,
  Checkbox,
  Select,
  TextField,
  MenuItem,
  InputAdornment,
  FormControl,
  useTheme,
  useMediaQuery,
  makeStyles
} from '@material-ui/core';
import { DateRangeOutlined } from '@material-ui/icons';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      marginBottom: 84,
      [theme.breakpoints.down('xs')]: {
        marginBottom: 100
      }
    },
    displayItem: {
      '& +div': {
        marginTop: 64
      },
      '& .MuiSelect-root': {
        width: 450,
        [theme.breakpoints.down('xs')]: {
          width: '100%'
        }
      },
      '& .MuiInputAdornment-root': {
        color: colorSet.gray600
      },
      '& .duedate': {
        display: 'flex',
        alignItems: 'center',
        marginTop: 12,
        '& .MuiCheckbox-root': {
          padding: 0,
          paddingRight: 9
        }
      },
      '& .budget-wrapper': {
        display: 'flex',
        alignItems: 'center',
        '& .divider-text': {
          margin: '0px 16px'
        },
        '& .budget-item': {
          display: 'flex',
          alignItems: 'center',
          '& .MuiTextField-root': {
            marginLeft: 8
          }
        }
      },
      '& .radio-btn-wrapper': {
        display: 'flex',
        alignItems: 'center'
      }
    }
  };
});

interface Props {
  onClick: () => void;
}

const ProjectInfoPresenter: React.FC<Props> = ({ onClick }) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <CustomizedTypography
        variant={matches ? 'h3' : 'h2'}
        fontWeight="bold"
        color="textPrimary"
        align="center"
        gutterBottoms={40}
      >
        프로젝트 정보를 입력해주세요
      </CustomizedTypography>
      <div className={classes.displayItem}>
        <CustomizedTypography
          variant="body1"
          fontWeight="500"
          color="textPrimary"
          gutterBottoms={4}
        >
          프로젝트명 *
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textSecondary"
          gutterBottoms={12}
        >
          한 문장으로 프로젝트를 설명할 수 있도록 작성해주세요 (최대 20자)
        </CustomizedTypography>
        <TextField
          placeholder="프로젝트명"
          variant="outlined"
          size="small"
          fullWidth
          onClick={onClick}
        />
      </div>
      <div className={classes.displayItem}>
        <CustomizedTypography
          variant="body1"
          fontWeight="500"
          color="textPrimary"
          gutterBottoms={4}
        >
          제품용도 *
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textSecondary"
          gutterBottoms={12}
        >
          제작하려는 제품의 산업 분야를 선택해주세요
        </CustomizedTypography>
        <FormControl
          size="small"
          variant="outlined"
          fullWidth={matches ? true : false}
          onClick={(e) => {
            onClick();
          }}
        >
          <Select defaultValue="제품용도" open={false}>
            <MenuItem value="제품용도" disabled>
              제품용도
            </MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className={classes.displayItem}>
        <CustomizedTypography
          variant="body1"
          fontWeight="500"
          color="textPrimary"
          gutterBottoms={4}
        >
          납기일 *
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textSecondary"
          gutterBottoms={12}
        >
          가공 업체의 상황에 따라 가공 가능한 납기일이 다를 수 있습니다. 납기일
          협의가 가능한 경우, 더 많은 견적을 받아 비교할 수 있습니다.
        </CustomizedTypography>
        <TextField
          placeholder="납기일을 선택해주세요."
          variant="outlined"
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <DateRangeOutlined />
              </InputAdornment>
            )
          }}
          fullWidth
          onClick={onClick}
        />
        <div className="duedate">
          <Checkbox onClick={onClick} />
          <CustomizedTypography variant="body2" color="textPrimary">
            납기일 협의가능
          </CustomizedTypography>
        </div>
      </div>
      <div className={classes.displayItem}>
        <CustomizedTypography
          variant="body1"
          fontWeight="500"
          color="textPrimary"
          gutterBottoms={4}
        >
          추정 예산 범위 *
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textSecondary"
          gutterBottoms={12}
        >
          추정 예산 범위를 작성해주세요. 예산 범위에 맞는 파트너를
          매칭해드립니다. 최대 천억 단위(숫자 12자리)까지 입력 가능합니다.
        </CustomizedTypography>
        <div className="budget-wrapper">
          <div className="budget-item">
            <CustomizedTypography
              variant="body2"
              fontWeight="500"
              color="textPrimary"
            >
              최소
            </CustomizedTypography>
            <TextField
              placeholder="숫자만 입력"
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CustomizedTypography>원</CustomizedTypography>
                  </InputAdornment>
                )
              }}
              onClick={onClick}
            />
          </div>
          <CustomizedTypography
            className="divider-text"
            variant="body2"
            fontWeight="500"
            color="textPrimary"
          >
            ~
          </CustomizedTypography>
          <div className="budget-item">
            <CustomizedTypography
              variant="body2"
              fontWeight="500"
              color="textPrimary"
            >
              최대
            </CustomizedTypography>
            <TextField
              placeholder="숫자만 입력"
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CustomizedTypography>원</CustomizedTypography>
                  </InputAdornment>
                )
              }}
              onClick={onClick}
            />
          </div>
        </div>
      </div>
      <div className={classes.displayItem}>
        <CustomizedTypography
          variant="body1"
          fontWeight="500"
          color="textPrimary"
          gutterBottoms={4}
        >
          추정 예산 공개여부 *
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textSecondary"
          gutterBottoms={12}
        >
          추정 예산 정보를 파트너에게 공개할지 선택합니다.
        </CustomizedTypography>
        <div className="radio-btn-wrapper">
          <Radio color="primary" checked={true} onClick={onClick} />
          <CustomizedTypography variant="body2" color="textPrimary">
            추정 예산을 파트너에게 공개합니다.
          </CustomizedTypography>
        </div>
        <div className="radio-btn-wrapper">
          <Radio color="primary" checked={false} onClick={onClick} />
          <CustomizedTypography variant="body2" color="textPrimary">
            추정 예산을 파트너에게 공개하지 않습니다.
          </CustomizedTypography>
        </div>
      </div>
    </div>
  );
};

export default ProjectInfoPresenter;
